import { IonApp, IonBadge, IonRouterOutlet, IonTabBar, IonTabButton, IonTabs, IonIcon, IonLabel, IonToast, setupIonicReact } from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import { Redirect, Route, useHistory, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { memo, useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { getReviewFullscreen, subscribeReviewFullscreen } from './lib/ui/reviewFullscreen.js';
import { hasPresentedOverlay } from './lib/ui/presentedOverlay.js';
import { App as CapApp } from '@capacitor/app';
import { initPushNotifications } from './lib/notifications/pushRegistration.js';
import { installGlobalErrorHandlers, startDebugFlusher } from './lib/debug/debugLog.js';
import { startAnalyticsFlusher, track } from './lib/analytics/track.js';
import { calendarOutline, cubeOutline, documentTextOutline, settingsOutline } from 'ionicons/icons';

import {
  AuthProvider,
  missingActiveBusinessTenantId,
  useAuth,
} from './lib/auth/AuthProvider.js';
import { SharedTabletOperatorProvider } from './lib/auth/SharedTabletOperatorProvider.js';
import { getSupabase } from './lib/supabase.js';
import { SyncProvider } from './lib/sync/SyncProvider.js';
import { getPendingShare, onShareReceived } from './lib/share/shareIntent.js';
import { createShareNavigationHandler } from './lib/share/shareNavigation.js';
import { CelebrationProvider } from './components/CelebrationToast.js';
import { useGlobalCounters } from './lib/haccp/counters.js';
import { useAccountAccessState, useDashboardConfigState } from './lib/data/queries.js';
import { isSurfaceVisibleForSubjects } from './lib/visibility/isSurfaceVisible.js';
import { isSurfaceRouteAllowed, routeSurfaceKeys } from './lib/visibility/routeAccess.js';
import { canConfigureCommercialPricing } from './lib/auth/capabilities.js';
import { useOperationalIdentity } from './lib/auth/useOperationalIdentity.js';

import { SignInPage } from './pages/auth/SignIn.js';
import { SignUpPage } from './pages/auth/SignUp.js';
import { BootstrapTenantPage } from './pages/auth/BootstrapTenant.js';
import { TrialExpiredPage } from './pages/auth/TrialExpiredPage.js';
import { SuspendedPage } from './pages/auth/SuspendedPage.js';
import PrepostoOrderPage from './pages/preposto/PrepostoOrderPage.js';
import PrepostoBedsPage from './pages/preposto/PrepostoBedsPage.js';
import RepartoPublicPage from './pages/preposto/RepartoPublicPage.js';
import PrepostoReviewPage from './pages/preposto/PrepostoReviewPage.js';
import PrepostoHomePage from './pages/preposto/PrepostoHomePage.js';
import PrepostoDdtPage from './pages/preposto/PrepostoDdtPage.js';
import PrepostoTracePage from './pages/preposto/PrepostoTracePage.js';
import { TodayPage } from './pages/today/TodayPage.js';
import { TemperaturesPage } from './pages/haccp/TemperaturesPage.js';
import { CleaningPage } from './pages/haccp/CleaningPage.js';
import { CleaningProductsPage } from './pages/haccp/CleaningProductsPage.js';
import { CleaningEquipmentsPage } from './pages/haccp/CleaningEquipmentsPage.js';
import { CleaningTodoPage } from './pages/haccp/CleaningTodoPage.js';
import { CleaningDonePage } from './pages/haccp/CleaningDonePage.js';
import { LabelsPage } from './pages/haccp/LabelsPage.js';
import { ExpirationsPage } from './pages/haccp/ExpirationsPage.js';
import { GuidePage } from './pages/GuidePage.js';
import { TraceSearchPage } from './pages/haccp/TraceSearchPage.js';
import { RecipesPage } from './pages/haccp/RecipesPage.js';
import { FoodCostPage } from './pages/haccp/FoodCostPage.js';
import { EventQuotesPage } from './pages/business/EventQuotesPage.js';
import { ManualePage } from './pages/haccp/ManualePage.js';
import { BusinessProfilePage } from './pages/haccp/BusinessProfilePage.js';
import { CalibrationsPage } from './pages/haccp/CalibrationsPage.js';
import { OilControlPage, AcrylamideControlPage } from './pages/haccp/ProcessControlPage.js';
import { PestControlPage } from './pages/haccp/PestControlPage.js';
import { RegistriPage } from './pages/haccp/RegistriPage.js';
import { CleaningRegisterVerificationPage } from './pages/haccp/CleaningRegisterVerificationPage.js';
import { RegistriCompilaPage } from './pages/haccp/RegistriCompilaPage.js';
import { FormazionePage } from './pages/haccp/FormazionePage.js';
import { NonConformitaPage } from './pages/haccp/NonConformitaPage.js';
import { ManutenzionePage } from './pages/haccp/ManutenzionePage.js';
import { TrasportoPage } from './pages/haccp/TrasportoPage.js';
import { WarehousePage } from './pages/warehouse/WarehousePage.js';
import { HistoricalLotsPage } from './pages/warehouse/HistoricalLotsPage.js';
import { ProductionsPage } from './pages/warehouse/ProductionsPage.js';
import { TraceViewPage } from './pages/warehouse/TraceViewPage.js';
// T-215 (modulo ristorazione collettiva, gated da tenants.collettiva_enabled)
import { RestaurantShipmentsPage, ShipmentsPage } from './pages/warehouse/ShipmentsPage.js';
import { ProductionDayPage } from './pages/warehouse/ProductionDayPage.js';
import { RestaurantTransferPage, TransferPage } from './pages/warehouse/TransferPage.js';
import { SupplyRequestsPage } from './pages/warehouse/SupplyRequestsPage.js';
import { ProductionSheetPage } from './pages/warehouse/ProductionSheetPage.js';
import ProductionPlanPage from './pages/warehouse/ProductionPlanPage.js';
import ProductionTodoPage from './pages/warehouse/ProductionTodoPage.js';
import ProductionDestinationsPage from './pages/warehouse/ProductionDestinationsPage.js';
import { DestinationsPage, TransportRecipientsPage } from './pages/settings/DestinationsPage.js';
import { TransportDocumentsPage } from './pages/documents/TransportDocumentsPage.js';
import { DocumentsPage } from './pages/documents/DocumentsPage.js';
import { DocumentDetailPage } from './pages/documents/DocumentDetailPage.js';
import { CapturePhotoPage } from './pages/documents/CapturePhotoPage.js';
import { SettingsPage } from './pages/settings/SettingsPage.js';
import { ReportsPage } from './pages/settings/ReportsPage.js';
import { BusinessDetailsPage } from './pages/settings/BusinessDetailsPage.js';
import { MenuPage } from './pages/settings/MenuPage.js';
import { MenuManagerPage } from './pages/settings/menu/MenuManagerPage.js';
import { MenuUploadsPage } from './pages/settings/MenuUploadsPage.js';
import { StaffPage } from './pages/settings/StaffPage.js';
import { AcceptInvitePage } from './pages/auth/AcceptInvitePage.js';
import { EmailVerifiedPage } from './pages/auth/EmailVerifiedPage.js';
import { PrinterConfigPage } from './pages/settings/PrinterConfigPage.js';
import { AuditPage } from './pages/settings/AuditPage.js';
import { SdiConfigPage } from './pages/settings/SdiConfigPage.js';
import { DiagnosticsPage } from './pages/settings/DiagnosticsPage.js';
import { OperationalEvidencePage } from './pages/settings/OperationalEvidencePage.js';
import { ConsultantPricingPage } from './pages/consultant/ConsultantPricingPage.js';

setupIonicReact({
  mode: 'md',
});

// These pages take no route props. Keep their mounted state without rebuilding
// the lists whenever Ionic updates an unrelated cached route. Auth, query and
// local state updates, and Ionic view lifecycle callbacks, remain active.
const MemoTodayPage = memo(TodayPage);
const MemoWarehousePage = memo(WarehousePage);
const MemoDocumentsPage = memo(DocumentsPage);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Listener Capacitor `appUrlOpen` per deep link da custom scheme
// `it.trackeat.app://...` (es. landing accept-invite.html). Estrae path+query
// e li gira al router React. Va montato dentro un IonReactRouter (usa
// useHistory), e duplicato in tutti i branch di AuthGate (loggato / non
// loggato / no-tenant) — altrimenti il link non funziona quando l'app si
// apre da freddo su un utente non autenticato.
function DeepLinkHandler(): null {
  const history = useHistory();
  useEffect(() => {
    const w = window as unknown as { __trackeatUrlOpenInstalled?: boolean };
    if (w.__trackeatUrlOpenInstalled) return;
    w.__trackeatUrlOpenInstalled = true;
    let urlHandle: ReturnType<typeof CapApp.addListener> | null = null;
    try {
      urlHandle = CapApp.addListener('appUrlOpen', ({ url }) => {
        try {
          const parsed = new URL(url);
          const isHttp = parsed.protocol === 'http:' || parsed.protocol === 'https:';

          // Ritorno dal login Google (Custom Tab → deep link PKCE).
          // `trackeat://auth-callback?code=…` oppure `?error=…`.
          const code = parsed.searchParams.get('code');
          const oauthErr = parsed.searchParams.get('error_description') ?? parsed.searchParams.get('error');
          const isAuthCallback =
            parsed.host === 'auth-callback' || parsed.pathname.includes('auth-callback') || Boolean(code);
          if (!isHttp && isAuthCallback) {
            void (async () => {
              try {
                const { Browser } = await import('@capacitor/browser');
                await Browser.close().catch(() => undefined);
              } catch { /* plugin assente: noop */ }
              if (oauthErr) {
                console.warn('[oauth] errore Google:', oauthErr);
                history.replace('/auth/signin');
                return;
              }
              if (!code) return;
              try {
                const { error } = await getSupabase().auth.exchangeCodeForSession(code);
                if (error) throw error;
                // Sessione attiva: AuthGate instrada da solo (bootstrap o app).
                // Se c'è un invito staff pendente, vai alla pagina accept.
                let pendingInvite: string | null = null;
                try { pendingInvite = localStorage.getItem('trackeat.pending_invite_token'); } catch { /* noop */ }
                history.replace(pendingInvite ? '/accept-invite' : '/today');
              } catch (err) {
                console.warn('[oauth] exchangeCodeForSession fallito:', err);
                history.replace('/auth/signin');
              }
            })();
            return;
          }

          const tail = parsed.pathname && parsed.pathname !== '/' ? parsed.pathname : '';
          const path = isHttp ? parsed.pathname : `/${parsed.host}${tail}`;
          history.push(`${path}${parsed.search}`);
        } catch (err) {
          console.warn('[appUrlOpen] URL non parsabile:', url, err);
        }
      });
    } catch (err) {
      console.warn('[appUrlOpen] @capacitor/app non disponibile', err);
    }
    return () => {
      if (urlHandle) void urlHandle.then((h) => h.remove()).catch(() => undefined);
      w.__trackeatUrlOpenInstalled = false;
    };
  }, [history]);
  return null;
}

function ConsultantPricingRoute(): JSX.Element {
  const { profile } = useAuth();
  return canConfigureCommercialPricing(profile)
    ? <ConsultantPricingPage />
    : <Redirect to="/today" />;
}

function AuthGate(): JSX.Element {
  const { session, loading, profile, switchTenant, switchBusiness } = useAuth();
  const { t } = useTranslation();
  const recoveryTenantId = profile?.active_tenant_id == null && (profile?.tenants.length ?? 0) > 0
    ? profile!.tenants[0]
    : null;
  const recoveryBusinessTenantId = missingActiveBusinessTenantId(profile);
  const [recoveryError, setRecoveryError] = useState<string | null>(null);

  const recoverActiveContext = useCallback(async (): Promise<void> => {
    if (!recoveryTenantId && !recoveryBusinessTenantId) return;
    setRecoveryError(null);
    try {
      if (recoveryTenantId) {
        await switchTenant(recoveryTenantId);
        return;
      }
      const { data, error } = await getSupabase()
        .from('businesses')
        .select('id')
        .eq('tenant_id', recoveryBusinessTenantId!)
        .is('archived_at', null)
        .order('created_at', { ascending: true })
        .order('id', { ascending: true })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      if (!data) throw new Error('active_business_missing');
      await switchBusiness((data as { id: string }).id);
    } catch {
      setRecoveryError('Impossibile ripristinare il locale attivo. Riprova quando sei online.');
    }
  }, [recoveryBusinessTenantId, recoveryTenantId, switchBusiness, switchTenant]);

  useEffect(() => {
    if (loading || !session || (!recoveryTenantId && !recoveryBusinessTenantId)) return;
    void recoverActiveContext();
  }, [loading, recoveryBusinessTenantId, recoveryTenantId, recoverActiveContext, session]);

  // [DISABILITATO 2026-05-07]: la richiesta permessi all'avvio causava crash
  // al secondo prompt (probabilmente PushNotifications.register() su device
  // senza FCM correttamente configurato, ma il try/catch non lo intercettava
  // a livello nativo — il plugin Android propaga l'eccezione fuori dalla
  // promise JS).
  // I permessi vengono richiesti just-in-time:
  // - Camera/Photos: al primo tap su "scatta foto" in CapturePhotoPage
  // - LocalNotifications: in expirationNotifier al primo schedule
  // - Push: solo se l'utente attiva esplicitamente da Settings (futuro)
  // useEffect(() => { ... requestStartupPermissions ... }, [session]);

  // T-438/T-441 — Pagina PUBBLICA "ordine per letto" del reparto (aperta dal QR
  // dopo il redirect della Pages Function). DEVE funzionare senza login E anche se
  // un utente è già loggato → short-circuit PRIMA di QUALSIASI gate (persino del
  // loading della sessione: un reparto non ha sessione e non deve mai vedere
  // "Caricamento" per colpa dell'auth). È pubblica per design (token + PIN validati
  // server-side dalla Pages Function), quindi non tocca sessione/tenant. La `key`
  // "reparto" è un literal fisso (come il fix T-428) e il path è stabile per tutta
  // la visita → nessun rimonta a metà. Su nativo il path non è mai /reparto → skip.
  if (/^\/reparto\/[a-z0-9]{20,64}$/.test(window.location.pathname)) {
    return (
      <IonReactRouter key="reparto">
        <IonRouterOutlet>
          <Route exact path="/reparto/:token" component={RepartoPublicPage} />
        </IonRouterOutlet>
      </IonReactRouter>
    );
  }

  if (loading) {
    return (
      <div style={{ padding: 24 }} aria-label={t('common.loading')}>
        {t('common.loading')}
      </div>
    );
  }

  // T-428 — FIX FREEZE login/logout. AuthGate ritorna un <IonReactRouter> in OGNI
  // branch: senza `key`, React RICONCILIA la stessa istanza router (stesso tipo,
  // stessa posizione) invece di rimontarla al cambio auth-mode. L'IonRouterOutlet
  // tiene in cache la vista corrente e la sua history resta sull'URL vecchio
  // (createBrowserHistory gira una volta sola nel costruttore) → il <Redirect> del
  // nuovo branch non scatta → schermata FREEZATA finché non si riavvia l'app.
  // Fix: una `key` LETTERALE FISSA per-mode forza il rimonta pulito SOLO ai 6 veri
  // cambi di modalità. ⚠️ La key DEVE restare un literal per-mode: MAI derivarla da
  // session/profile/access_token/active_tenant_id, altrimenti il refresh token
  // (orario + a ogni resume) o lo switch tenant rimonterebbero il router a metà
  // sessione buttando l'utente sulla route di default (/today) e perdendo la
  // navigazione. Sei literal, uno per mode: è tutto il fix.
  if (!session) {
    return (
      <IonReactRouter key="anon">
        <DeepLinkHandler />
        <IonRouterOutlet>
          <Route exact path="/auth/signin" component={SignInPage} />
          <Route exact path="/auth/signup" component={SignUpPage} />
          <Route exact path="/auth/verified" component={EmailVerifiedPage} />
          <Route exact path="/auth/bootstrap" component={BootstrapTenantPage} />
          <Route exact path="/accept-invite" component={AcceptInvitePage} />
          <Redirect exact from="/" to="/auth/signin" />
          <Route render={() => <Redirect to="/auth/signin" />} />
        </IonRouterOutlet>
      </IonReactRouter>
    );
  }

  // Il server usa `tenant_id:null` per revocare in modo fail-closed un tenant
  // attivo non più valido. Se restano altre membership, selezionane una tramite
  // l'RPC autorizzato e rinnova il JWT prima di montare l'app operativa.
  if (recoveryTenantId || recoveryBusinessTenantId) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: recoveryError ? '#b91c1c' : '#64748b' }}>
        <div>{recoveryError ?? 'Ripristino del locale attivo…'}</div>
        {recoveryError && <button type="button" onClick={() => { void recoverActiveContext(); }} style={{ marginTop: 12 }}>Riprova</button>}
      </div>
    );
  }

  // T-282: PREPOSTO (utente esterno scoped a UNA destinazione di collettiva).
  // Ha `tenants: []` (fail-closed lato server) → senza questo branch finirebbe
  // al bootstrap. Shell minimale: solo il suo ordine + cassetto DDT. DEVE stare
  // PRIMA del check "no tenant" qui sotto.
  if (profile?.preposto) {
    return (
      <IonReactRouter key="preposto">
        <DeepLinkHandler />
        <IonRouterOutlet>
          <Route exact path="/preposto" component={PrepostoHomePage} />
          <Route exact path="/preposto/ordini" component={PrepostoOrderPage} />
          <Route exact path="/preposto/ordini-letto" component={PrepostoBedsPage} />
          <Route exact path="/preposto/riepilogo" component={PrepostoReviewPage} />
          <Route exact path="/preposto/ddt" component={PrepostoDdtPage} />
          <Route exact path="/preposto/tracciabilita" component={PrepostoTracePage} />
          <Redirect to="/preposto" />
        </IonRouterOutlet>
      </IonReactRouter>
    );
  }

  // Logged in but no tenant yet → force bootstrap, A MENO CHE non ci sia
  // un token di invito pendente in localStorage (T-063): in quel caso
  // l'utente è arrivato qui via /accept-invite e va lì, non a bootstrap.
  // Se va a bootstrap, crea un tenant duplicato che orfana l'ambiente
  // pre-popolato dal consulente.
  if (!profile?.tenants || profile.tenants.length === 0) {
    let pendingInvite: string | null = null;
    try { pendingInvite = localStorage.getItem('trackeat.pending_invite_token'); } catch { /* noop */ }
    const hasPendingInvite = !!(pendingInvite && pendingInvite.length >= 32);
    return (
      <IonReactRouter key="bootstrap">
        <DeepLinkHandler />
        <IonRouterOutlet>
          <Route exact path="/auth/bootstrap" component={BootstrapTenantPage} />
          <Route exact path="/auth/verified" component={EmailVerifiedPage} />
          <Route exact path="/accept-invite" component={AcceptInvitePage} />
          <Redirect to={hasPendingInvite ? '/accept-invite' : '/auth/bootstrap'} />
        </IonRouterOutlet>
      </IonReactRouter>
    );
  }

  // Account bloccato → hard-block (T-019 trial + T-266 sospensione). Il claim
  // `trial_active` (fail-closed: false per QUALSIASI motivo) e `account_block_reason`
  // sono popolati dai JWT hook e tengono già conto dell'override owner. Sessioni
  // pre-T-019 hanno `trial_active=true` di default in profileFromSession (non
  // bloccare per cache obsoleta; il refresh JWT porta i valori corretti).
  if (profile?.trial_active === false || profile?.account_block_reason != null) {
    // Motivo preciso dal claim T-266; se assente (token pre-T-266) ma il blocco
    // c'è, è un trial scaduto → TrialExpiredPage.
    const blockReason = profile?.account_block_reason ?? 'trial_expired';
    if (blockReason === 'suspended') {
      return (
        <IonReactRouter key="suspended">
          <DeepLinkHandler />
          <IonRouterOutlet>
            <Route exact path="/account-suspended" component={SuspendedPage} />
            <Redirect to="/account-suspended" />
          </IonRouterOutlet>
        </IonReactRouter>
      );
    }
    return (
      <IonReactRouter key="trial">
        <DeepLinkHandler />
        <IonRouterOutlet>
          <Route exact path="/trial-expired" component={TrialExpiredPage} />
          <Redirect to="/trial-expired" />
        </IonRouterOutlet>
      </IonReactRouter>
    );
  }

  return (
    <IonReactRouter key="app">
      <DeepLinkHandler />
      <AppTabs />
    </IonReactRouter>
  );
}

function AppTabs(): JSX.Element {
  const { t } = useTranslation();
  const counters = useGlobalCounters();
  const { profile } = useAuth();
  const dashboardState = useDashboardConfigState();
  const accountAccess = useAccountAccessState();
  const operationalIdentity = useOperationalIdentity();
  const dashboardConfig = dashboardState.config;
  const visibilitySubjects = operationalIdentity.subjects;
  const visibilityResolved = dashboardState.resolved && accountAccess.resolved;
  const isConsultantOrOwner = profile?.is_platform_owner === true || profile?.role === 'consulente';
  const location = useLocation();
  const showWarehouseTab = isConsultantOrOwner || (visibilityResolved && isSurfaceVisibleForSubjects(dashboardConfig, visibilitySubjects, 'tab.warehouse'));
  const showDocumentsTab = isConsultantOrOwner || (visibilityResolved && isSurfaceVisibleForSubjects(dashboardConfig, visibilitySubjects, 'tab.documents'));
  const history = useHistory();
  // T-151: la tab bar resta visibile mentre scegli/scatti la foto e lanci
  // l'analisi; sparisce SOLO quando compare l'esito IA (fornitore + righe), per
  // dare al wizard tutta l'altezza. Il flag è acceso da CapturePhotoPage quando
  // il dry_run è pronto (vedi lib/ui/reviewFullscreen).
  const hideTabBar = useSyncExternalStore(subscribeReviewFullscreen, getReviewFullscreen);
  const [exitToast, setExitToast] = useState(false);
  const exitReady = useRef(false);
  const exitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const routes: Record<string, string> = {
      today: '/today',
      warehouse: '/warehouse',
      documents: '/documents',
      settings: '/settings',
    };
    const navigateFromTab = (event: MouseEvent) => {
      if (!(event.target instanceof Element)) return;
      const tabButton = event.target.closest('ion-tab-bar ion-tab-button');
      const tab = tabButton?.getAttribute('tab');
      const route = tab ? routes[tab] : undefined;
      if (!route) return;

      event.preventDefault();
      event.stopPropagation();
      track('feature_open', { tab });
      if (history.location.pathname !== route || history.location.search) history.push(route);
    };

    document.addEventListener('click', navigateFromTab, { capture: true });
    return () => document.removeEventListener('click', navigateFromTab, { capture: true });
  }, [history]);

  useEffect(() => {
    installGlobalErrorHandlers();
    startDebugFlusher();
    startAnalyticsFlusher();
    void initPushNotifications();
  }, []);

  // "Premi ancora per uscire" — intercetta il back nativo Android via @capacitor/app.
  // canGoBack=false → siamo sul fondo della history (tab root senza modal aperti).
  //
  // Nota: su Android, quando la camera / file picker chiude la sua Activity e
  // restituisce il controllo alla WebView, alcune versioni sparano un evento
  // backButton spurio durante il resume. Il cooldown di 800ms dopo appStateChange
  // impedisce che questo back spurio navighi via dalla pagina corrente.
  const lastResumeAt = useRef(0);
  useEffect(() => {
    // Guard contro accumulo listener su HMR/live-reload.
    const w = window as unknown as { __niveoBackInstalled?: boolean };
    if (w.__niveoBackInstalled) return;
    w.__niveoBackInstalled = true;

    // @capacitor/app potrebbe non essere stato sincronizzato lato nativo
    // (richiede `npx cap sync android` dopo l'install). In quel caso le
    // addListener rifiutano con "App plugin is not implemented on android".
    // Wrappiamo in try/catch così l'app continua a funzionare: la WebView
    // Android gestirà il back nativamente, modali Ionic inclusi.
    let resumeHandle: ReturnType<typeof CapApp.addListener> | null = null;
    let backHandle: ReturnType<typeof CapApp.addListener> | null = null;

    // T-092: helper di navigazione condiviso fra polling (cold-start) e
    // listener push (warm/hot). Costruisce la query string e naviga al
    // wizard CapturePhotoPage che pre-carica il file via Filesystem.
    let active = true;
    const navigateToShareImport = createShareNavigationHandler((location) => {
      if (active) history.push(location);
    });

    // Polling: copre il cold-start (app appena lanciata da Share Intent —
    // il bridge non era ancora attached quando MainActivity.onCreate ha
    // salvato in SharedPreferences, quindi nessun evento è stato emesso).
    const checkPendingShare = async () => {
      const share = await getPendingShare();
      if (share) navigateToShareImport(share);
    };

    // Listener push: copre il caso app già viva (foreground o background)
    // che riceve un nuovo Intent.ACTION_SEND tramite onNewIntent. In quel
    // caso `appStateChange` non scatta (lo stato non cambia, era già attivo),
    // quindi il polling sopra non aiuta — serve l'evento attivo dal plugin.
    // NB: salviamo la Promise (non il valore risolto). Sotto StrictMode/HMR
    // la cleanup può girare prima che `.then` risolva; il pattern Promise
    // permette al cleanup di chainare `.then((h) => h.remove())` comunque,
    // come fanno resumeHandle/backHandle sotto.
    const shareHandle: ReturnType<typeof onShareReceived> = onShareReceived((share) => {
      navigateToShareImport(share);
    });

    try {
      resumeHandle = CapApp.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          lastResumeAt.current = Date.now();
          // T-092: ricontrolla pending share quando l'app torna in primo piano
          // (es. utente in TrackEAT → apre Mail → tap Condividi → torna su TrackEAT).
          void checkPendingShare();
        }
      });
    } catch (err) {
      console.warn('[backHandler] @capacitor/app appStateChange non disponibile', err);
    }
    // T-092: check anche al primo mount (cold-start tramite condivisione).
    void checkPendingShare();
    try {
      backHandle = CapApp.addListener('backButton', ({ canGoBack }) => {
      if (Date.now() - lastResumeAt.current < 1500) return;
      if (hasPresentedOverlay()) {
        return;
      }
      if (canGoBack) {
        window.history.back();
        return;
      }
      // Fondo della history: primo back mostra toast, secondo back esce
      if (exitReady.current) {
        exitReady.current = false;
        if (exitTimer.current) clearTimeout(exitTimer.current);
        try { void CapApp.exitApp(); } catch { /* plugin missing */ }
        return;
      }
      setExitToast(true);
      exitReady.current = true;
      exitTimer.current = setTimeout(() => { exitReady.current = false; }, 2000);
      });
    } catch (err) {
      console.warn('[backHandler] @capacitor/app backButton non disponibile', err);
    }
    return () => {
      active = false;
      if (resumeHandle) void resumeHandle.then((h) => h.remove()).catch(() => undefined);
      if (backHandle) void backHandle.then((h) => h.remove()).catch(() => undefined);
      void shareHandle.then((h) => h.remove()).catch(() => undefined);
      if (exitTimer.current) clearTimeout(exitTimer.current);
      const w = window as unknown as { __niveoBackInstalled?: boolean };
      w.__niveoBackInstalled = false;
    };
  }, []);
  // T-013: dashboard unica - il badge "Oggi" riassume tutto quello che prima
  // era splittato fra i tab "Oggi" e "HACCP" (overdueTemps era esclusivo del
  // secondo). Niente più tab HACCP, niente più badge separato.
  const todayCount =
    counters.pendingAlerts +
    counters.expiringSoon +
    counters.overdueCleanings +
    counters.overdueTemps;

  const guardedSurfaceKeys = routeSurfaceKeys(location.pathname);
  if (guardedSurfaceKeys && !visibilityResolved && !isConsultantOrOwner) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: '#64748b' }}>
        {dashboardState.unavailable || accountAccess.unavailable
          ? 'Accesso non verificabile senza rete. Torna a Oggi e riprova quando sei online.'
          : 'Verifica accesso…'}
      </div>
    );
  }

  if (!isSurfaceRouteAllowed({
    pathname: location.pathname,
    config: dashboardConfig,
    subjects: visibilitySubjects,
    bypassVisibility: isConsultantOrOwner,
    configResolved: visibilityResolved,
  })) {
    return <Redirect to="/today" />;
  }

  return (
    <IonTabs>
        <IonRouterOutlet>
          <Route exact path="/today" render={() => <MemoTodayPage />} />
          <Route exact path="/consultant/pricing" component={ConsultantPricingRoute} />
          {/* T-013: la home HACCP è ora assorbita da TodayPage; /haccp
              resta come deep-link redirect per non rompere link esistenti. */}
          <Redirect exact from="/haccp" to="/today" />
          <Route exact path="/haccp/temperatures" component={TemperaturesPage} />
          <Route exact path="/haccp/cleaning" component={CleaningPage} />
          <Route exact path="/haccp/cleaning/products" component={CleaningProductsPage} />
          <Route exact path="/haccp/cleaning/equipments" component={CleaningEquipmentsPage} />
          <Route exact path="/haccp/cleaning/todo" component={CleaningTodoPage} />
          <Route exact path="/haccp/cleaning/done" component={CleaningDonePage} />
          <Route exact path="/haccp/recipes" component={RecipesPage} />
          <Route exact path="/haccp/food-cost" component={FoodCostPage} />
          <Route exact path={['/business/event-quotes', '/business/event-quotes/new', '/business/event-quotes/:eventId/edit']} component={EventQuotesPage} />
          <Route exact path="/haccp/labels" component={LabelsPage} />
          <Route exact path="/haccp/expirations" component={ExpirationsPage} />
          <Route exact path="/haccp/trace-search" component={TraceSearchPage} />
          <Route exact path="/haccp/manuale" component={ManualePage} />
          <Route exact path="/guida" component={GuidePage} />
          <Route exact path="/haccp/manuale/profilo" component={BusinessProfilePage} />
          <Route exact path="/haccp/calibrations" component={CalibrationsPage} />
          <Route exact path="/haccp/pest-control" component={PestControlPage} />
          <Route exact path="/haccp/oil-control" component={OilControlPage} />
          <Route exact path="/haccp/acrylamide-control" component={AcrylamideControlPage} />
          <Route exact path="/haccp/registri" component={RegistriPage} />
          <Route exact path="/haccp/registri/verifica-pulizie" component={CleaningRegisterVerificationPage} />
          <Route exact path="/haccp/registri-compila" component={RegistriCompilaPage} />
          <Route exact path="/haccp/formazione" component={FormazionePage} />
          <Route exact path="/haccp/non-conformita" component={NonConformitaPage} />
          <Route exact path="/haccp/manutenzione" component={ManutenzionePage} />
          <Route exact path="/haccp/trasporto" component={TrasportoPage} />
          <Route exact path="/warehouse" render={() => <MemoWarehousePage />} />
          <Route exact path="/warehouse/historical-lots" component={HistoricalLotsPage} />
          <Route exact path="/warehouse/productions" component={ProductionsPage} />
          <Route exact path="/warehouse/trace/:token([0-9a-fA-F]{16,64})" component={TraceViewPage} />
          {/* T-215 collettiva: le pagine si auto-gatano su useCollettivaEnabled() */}
          <Route exact path="/warehouse/shipments" component={ShipmentsPage} />
          <Route exact path="/warehouse/production-day" component={ProductionDayPage} />
          <Route exact path="/warehouse/transfer" component={TransferPage} />
          <Route exact path="/warehouse/supply-requests" component={SupplyRequestsPage} />
          <Route exact path="/warehouse/production-plan" component={ProductionPlanPage} />
          <Route exact path="/warehouse/production-todo" component={ProductionTodoPage} />
          <Route exact path="/warehouse/production-destinations" component={ProductionDestinationsPage} />
          <Route exact path="/warehouse/production-sheet" component={ProductionSheetPage} />
          {/* T-277 aggancerà il tile dashboard del responsabile a /warehouse/supply-requests */}
          {/* T-563: modulo DDT ristorante autonomo; ogni pagina applica anche un gate server fresh. */}
          <Route exact path="/documents/transport" component={TransportDocumentsPage} />
          <Route exact path="/documents/transport/sales" component={RestaurantShipmentsPage} />
          <Route exact path="/documents/transport/internal" component={RestaurantTransferPage} />
          <Route exact path="/documents/transport/recipients" component={TransportRecipientsPage} />
          <Route exact path="/documents" render={() => <MemoDocumentsPage />} />
          <Route exact path="/documents/capture" component={CapturePhotoPage} />
          <Route exact path="/documents/:source(fiscal|public)/:id([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})" component={DocumentDetailPage} />
          <Route exact path="/settings" component={SettingsPage} />
          <Route exact path="/settings/reports" component={ReportsPage} />
          <Route exact path="/settings/business" component={BusinessDetailsPage} />
          <Route exact path="/settings/menu" component={MenuPage} />
          <Route exact path="/settings/menu/manage" component={MenuManagerPage} />
          <Route exact path="/settings/menu/uploads" component={MenuUploadsPage} />
          <Route exact path="/settings/staff" component={StaffPage} />
          <Route exact path="/settings/operational-evidence" component={OperationalEvidencePage} />
          <Route exact path="/settings/destinations" component={DestinationsPage} />
          <Route exact path="/accept-invite" component={AcceptInvitePage} />
          <Route exact path="/settings/printer" component={PrinterConfigPage} />
          {/* T-148: pagina "Abbattimento — template" rimossa. L'abbattimento si
              configura ora solo sulla ricetta (RecipesPage). La rotta resta come
              redirect per non rompere eventuali link/tile residui. */}
          <Redirect exact from="/settings/blast-chill" to="/settings" />
          <Route exact path="/settings/audit" component={AuditPage} />
          <Route exact path="/settings/sdi" component={SdiConfigPage} />
          <Route exact path="/settings/diagnostics" component={DiagnosticsPage} />
          <Redirect exact from="/" to="/today" />
          <Route render={() => <Redirect to="/today" />} />
        </IonRouterOutlet>

        <IonTabBar slot="bottom" style={hideTabBar ? { display: 'none' } : undefined}>
          <IonTabButton
            tab="today"
            href="/today"
          >
            <IonIcon icon={calendarOutline} />
            <IonLabel>{t('nav.today')}</IonLabel>
            {todayCount > 0 && <IonBadge color="danger">{todayCount}</IonBadge>}
          </IonTabButton>
          {/* NB (T-318): NON rendere condizionali gli IonTabButton dentro IonTabBar
              (es. `{cond && <IonTabButton/>}`): Ionic enumera i figli del tab bar
              per costruire le rotte e un figlio assente/`false` fa crashare il
              guscio al primo render → l'app muore al boot prima di `ready()` →
              rollback OTA a 0.0.0 (incidente 2026-06-27, bundle 191). Si rende
              SEMPRE il bottone e lo si nasconde con `display:none`: il set di
              figli resta statico, la rotta intatta, ma lo staff non lo vede. */}
          <IonTabButton
            tab="warehouse"
            href="/warehouse"
            style={showWarehouseTab ? undefined : { display: 'none' }}
          >
            <IonIcon icon={cubeOutline} />
            <IonLabel>{t('nav.warehouse')}</IonLabel>
            {counters.expiringSoon > 0 && <IonBadge color="warning">{counters.expiringSoon}</IonBadge>}
          </IonTabButton>
          <IonTabButton
            tab="documents"
            href="/documents"
            style={showDocumentsTab ? undefined : { display: 'none' }}
          >
            <IonIcon icon={documentTextOutline} />
            <IonLabel>{t('nav.documents')}</IonLabel>
          </IonTabButton>
          <IonTabButton
            tab="settings"
            href="/settings"
          >
            <IonIcon icon={settingsOutline} />
            <IonLabel>{t('nav.settings')}</IonLabel>
          </IonTabButton>
        </IonTabBar>
        <IonToast
          isOpen={exitToast}
          message="Premi ancora per uscire dall'app"
          duration={2000}
          position="bottom"
          onDidDismiss={() => setExitToast(false)}
        />
    </IonTabs>
  );
}

export function App(): JSX.Element {
  return (
    <IonApp>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <SharedTabletOperatorProvider>
            <SyncProvider>
              <CelebrationProvider>
                <AuthGate />
              </CelebrationProvider>
            </SyncProvider>
          </SharedTabletOperatorProvider>
        </AuthProvider>
      </QueryClientProvider>
    </IonApp>
  );
}
